import React from 'react';
import { useTranslation } from 'react-i18next';
import '../styles/LegalPages.css';

// ── Block type definitions ──────────────────────────────────────────────────

interface ClauseBlock {
  type: 'clause';
  num: string;
  html: string;
}

interface BenefitsTableBlock {
  type: 'benefits-table';
  headers: [string, string];
  rows: [string, string][];
}

interface PriceTableRow {
  planHtml: string;
  autoTag: string;
  details: string;
}

interface PriceTableBlock {
  type: 'price-table';
  headers: [string, string];
  rows: PriceTableRow[];
}

interface WarningBlock {
  type: 'warning';
  html: string;
}

interface SubListBlock {
  type: 'sub-list';
  items: string[];
}

interface ContactItem {
  label: string;
  href?: string;
  value: string;
}

interface ContactCardBlock {
  type: 'contact-card';
  items: ContactItem[];
}

type AgreementBlock =
  | ClauseBlock
  | BenefitsTableBlock
  | PriceTableBlock
  | WarningBlock
  | SubListBlock
  | ContactCardBlock;

interface AgreementSection {
  num: string;
  title: string;
  blocks: AgreementBlock[];
}

interface NoticeData {
  title: string;
  body: string;
  strong: string;
}

interface MemberAgreementData {
  heroTitle: string;
  heroMeta: string;
  notice: NoticeData;
  sections: AgreementSection[];
  footerNote: string;
}

// ── Block renderer ──────────────────────────────────────────────────────────

const renderBlock = (block: AgreementBlock, index: number): React.ReactNode => {
  switch (block.type) {
    case 'clause':
      return (
        <div key={index} className="ma-clause">
          {block.num && <span className="ma-clause-num">{block.num}</span>}{' '}
          {/* Content is our own legal text – no user input. */}
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: block.html }} />
        </div>
      );

    case 'benefits-table':
      return (
        <table key={index} className="ma-benefits-table">
          <thead>
            <tr>
              <th>{block.headers[0]}</th>
              <th>{block.headers[1]}</th>
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                {/* eslint-disable-next-line react/no-danger */}
                <td dangerouslySetInnerHTML={{ __html: row[0] }} />
                {/* eslint-disable-next-line react/no-danger */}
                <td dangerouslySetInnerHTML={{ __html: row[1] }} />
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'price-table':
      return (
        <table key={index} className="ma-price-table">
          <thead>
            <tr>
              <th>{block.headers[0]}</th>
              <th>{block.headers[1]}</th>
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                <td>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: row.planHtml }} />
                  <span className="ma-auto-tag">{row.autoTag}</span>
                </td>
                <td>{row.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

    case 'warning':
      return (
        <div key={index} className="ma-warning">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: block.html }} />
        </div>
      );

    case 'sub-list':
      return (
        <ul key={index} className="ma-sub-list">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );

    case 'contact-card':
      return (
        <div key={index} className="ma-contact-card">
          {block.items.map((item, i) => (
            <div key={i} className="ma-contact-item">
              <span className="ma-contact-label">{item.label}</span>
              {item.href ? (
                <a href={item.href} className="ma-contact-link">{item.value}</a>
              ) : (
                <span>{item.value}</span>
              )}
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

// ── Page component ──────────────────────────────────────────────────────────

const MemberAgreementPage: React.FC = () => {
  const { t } = useTranslation();

  const data = t('memberAgreement', { returnObjects: true }) as MemberAgreementData;

  if (!data || !Array.isArray(data.sections)) {
    return (
      <div className="ma-page">
        <div className="ma-hero-banner">
          <div className="ma-hero-banner-inner">
            <div className="ma-doc-title">Loading…</div>
          </div>
        </div>
      </div>
    );
  }

  const { heroTitle, heroMeta, notice, sections, footerNote } = data;

  return (
    <div className="ma-page">
      {/* Hero Banner */}
      <div className="ma-hero-banner">
        <div className="ma-hero-banner-inner">
          <div className="ma-doc-title">{heroTitle}</div>
          <div className="ma-doc-meta">{heroMeta}</div>
        </div>
      </div>

      <div className="ma-content">
        {/* Notice */}
        <div className="ma-notice">
          <strong>{notice.title}</strong>
          {notice.body}
          <br /><br />
          <strong>{notice.strong}</strong>
        </div>

        {/* Sections */}
        {sections.map((section) => (
          <div key={section.num} className="ma-section">
            <div className="ma-section-title">
              <span className="ma-section-num">{section.num}</span>
              {section.title}
            </div>
            {section.blocks.map((block, i) => renderBlock(block, i))}
          </div>
        ))}

        <p className="ma-end-note">{footerNote}</p>
      </div>
    </div>
  );
};

export default MemberAgreementPage;
